import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import api, { marksApi, classesApi, studentsApi, subjectsApi, reportCardApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { FileText, Save, Loader2, Download, AlertTriangle, Trash2 } from 'lucide-react';

// Only Term-1 and Term-2 — both are PT/SEA/PF/MA/MT(or AE) composites
const EXAM_TYPES = [
  { value: 'Term-1', label: 'Term-1 (PT-1 / SEA-1 / PF-1 / MA-1 / MT)', term: 1 },
  { value: 'Term-2', label: 'Term-2 (PT-2 / SEA-2 / PF-2 / MA-2 / AE)', term: 2 },
];

// For Class 6+: single 'Final' term replaces the Term-1/Term-2 split.
// Stored internally as 'Term-2' so all aggregation/marksheet logic continues to work.
const EXAM_TYPES_SINGLE = [
  { value: 'Term-2', label: 'Final (PT / SEA / PF / MA / AE)', term: 2 },
];

// Standard 6 scholastic subjects required on the marks card
const SCHOLASTIC_NAMES = ['english', 'hindi', 'kannada', 'mathematics', 'science', 'social studies'];

const COMPOSITE_CAPS = { pt: 5, sea: 5, pf: 5, ma: 5, mt: 80 };
const COMPOSITE_TOTAL = 100;
const num = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v) || 0);

const MarksPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [examType, setExamType] = useState('');

  // Composite form state: {studentId: {pt, sea, pf, ma, mt}}
  const [compositeData, setCompositeData] = useState({});
  // {studentId: {ready, missing}}
  const [reportStatus, setReportStatus] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const examMeta = EXAM_TYPES.find((e) => e.value === examType);
  const componentLabels = examMeta?.term === 2
    ? { pt: 'PT-2', sea: 'SEA 2', pf: 'PF 2', ma: 'MA 2', mt: 'AE' }
    : { pt: 'PT-1', sea: 'SEA 1', pf: 'PF 1', ma: 'MA 1', mt: 'MT' };

  // Whether the currently-selected class is single-term (Class 6+)
  const selectedClassDoc = classes.find((c) => c.id === selectedClass);
  const isSingleTerm = !!selectedClassDoc?.single_term;
  const examOptions = isSingleTerm ? EXAM_TYPES_SINGLE : EXAM_TYPES;
  const examLabel = isSingleTerm
    ? 'Final'
    : (examMeta?.term === 2 ? 'Term-2' : 'Term-1');

  useEffect(() => {
    (async () => {
      try {
        const r = await classesApi.getAll();
        setClasses(r.data.classes || []);
      } catch { toast.error('Failed to load classes'); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadStudentsAndSubjects = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      // Ensure 6 scholastic subjects exist for this class (idempotent)
      try {
        await api.post('/subjects/scholastic/ensure', null, { params: { class_id: selectedClass } });
      } catch (err) {
        // admin-only endpoint; ignored for non-admins
        console.debug('scholastic/ensure skipped:', err?.response?.status);
      }
      const [studentsRes, subjectsRes] = await Promise.all([
        studentsApi.getAll({ class_id: selectedClass, limit: 100 }),
        subjectsApi.getAll(selectedClass)
      ]);
      const studentList = studentsRes.data.students || [];
      setStudents(studentList);
      const allSubjects = subjectsRes.data.subjects || [];
      // Filter to scholastic 6 — case-insensitive name match
      const scholastic = SCHOLASTIC_NAMES
        .map((n) => allSubjects.find((s) => (s.name || '').trim().toLowerCase() === n))
        .filter(Boolean);
      setSubjects(scholastic);
      // Refresh report-card readiness for each student
      refreshReportStatus(studentList);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [selectedClass]);

  useEffect(() => { loadStudentsAndSubjects(); }, [loadStudentsAndSubjects]);

  // When subject + exam_type are picked, fetch any previously saved marks and pre-fill
  useEffect(() => {
    const loadExisting = async () => {
      if (!selectedClass || !selectedSubject || !examType) {
        setCompositeData({});
        return;
      }
      try {
        const res = await api.get('/marks/scope', {
          params: { class_id: selectedClass, subject_id: selectedSubject, exam_type: examType }
        });
        const map = {};
        Object.entries(res.data.by_student || {}).forEach(([sid, m]) => {
          map[sid] = {
            pt: m.pt ?? '', sea: m.sea ?? '', pf: m.pf ?? '',
            ma: m.ma ?? '', mt: m.mt ?? '',
          };
        });
        setCompositeData(map);
      } catch (err) {
        // Composite marks endpoint is optional; log for troubleshooting only
        console.debug('composite marks not available:', err?.response?.status);
      }
    };
    loadExisting();
  }, [selectedClass, selectedSubject, examType]);

  const refreshReportStatus = async (list) => {
    if (!list?.length) return;
    const results = await Promise.allSettled(
      list.map((s) => api.get(`/reportcard/${s.id}/status`))
    );
    const map = {};
    list.forEach((s, i) => {
      const r = results[i];
      if (r.status === 'fulfilled') map[s.id] = r.value.data;
      else map[s.id] = { ready: false, missing: [] };
    });
    setReportStatus(map);
  };

  const handleComponentChange = (studentId, key, value) => {
    const cap = COMPOSITE_CAPS[key];
    let v = value === '' ? '' : parseFloat(value);
    if (typeof v === 'number') {
      if (Number.isNaN(v) || v < 0) return;
      if (v > cap) v = cap;
    }
    setCompositeData((prev) => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), [key]: v },
    }));
  };

  const componentSum = (row) =>
    num(row?.pt) + num(row?.sea) + num(row?.pf) + num(row?.ma) + num(row?.mt);

  const handleSaveMarks = async () => {
    if (!selectedClass || !selectedSubject || !examType) {
      toast.error('Please select class, subject, and exam type');
      return;
    }

    const marksList = Object.entries(compositeData)
      .filter(([_id, row]) => componentSum(row) > 0)
      .map(([student_id, row]) => ({
        student_id,
        subject_id: selectedSubject,
        class_id: selectedClass,
        exam_type: examType,
        marks_obtained: componentSum(row),
        total_marks: COMPOSITE_TOTAL,
        pt: num(row.pt),
        sea: num(row.sea),
        pf: num(row.pf),
        ma: num(row.ma),
        mt: num(row.mt),
      }));

    if (marksList.length === 0) {
      toast.error('Enter marks for at least one student');
      return;
    }

    setSaving(true);
    try {
      const res = await marksApi.createBulk(marksList);
      toast.success(res.data?.message || `Saved ${marksList.length} entries`);
      refreshReportStatus(students);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save marks');
    } finally { setSaving(false); }
  };

  const handleResetEntries = async () => {
    if (!selectedClass || !selectedSubject || !examType) {
      toast.error('Pick class, subject, and exam type first');
      return;
    }
    const subj = subjects.find((s) => s.id === selectedSubject);
    if (!window.confirm(
      `Delete ALL ${examType} marks for ${subj?.name || 'this subject'} ` +
      `in this class? This cannot be undone.`
    )) return;
    setResetting(true);
    try {
      const res = await api.delete('/marks/scope', {
        params: { class_id: selectedClass, subject_id: selectedSubject, exam_type: examType }
      });
      toast.success(res.data?.message || 'Entries deleted');
      setCompositeData({});
      refreshReportStatus(students);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setResetting(false);
    }
  };

  const handleDownloadReport = async (student) => {
    const status = reportStatus[student.id];
    if (status && !status.ready) {
      toast.error(
        `Report card not ready. Missing: ${status.missing.slice(0, 3).join(', ')}` +
        (status.missing.length > 3 ? ` … (+${status.missing.length - 3} more)` : '')
      );
      return;
    }
    window.open(reportCardApi.downloadUrl(student.id), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6" data-testid="marks-page">
      <div>
        <h1 className="text-3xl font-heading font-bold">Marks Entry</h1>
        <p className="text-muted-foreground mt-1">
          {isSingleTerm ? (
            <>
              Class 6 and above use a single <b>Final</b> term. Marks card requires all{' '}
              <b>6 scholastic subjects</b> for <b>Final</b>. Enter PT, SEA, PF, MA &amp; AE
              per subject.
            </>
          ) : (
            <>
              Marks card requires all <b>6 scholastic subjects</b> × <b>Term-1</b> +{' '}
              <b>Term-2</b>. Enter PT, SEA, PF, MA &amp; MT/AE per subject — these
              aggregate exactly the way the report card shows them.
            </>
          )}
        </p>
      </div>

      <Card className="border border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="mb-2 block">Class</Label>
              <Select value={selectedClass || 'none'} onValueChange={(v) => setSelectedClass(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="marks-select-class">
                  <SelectValue placeholder="Select Class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select Class</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.section && `- ${c.section}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">Subject</Label>
              <Select value={selectedSubject || 'none'} onValueChange={(v) => setSelectedSubject(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="marks-select-subject">
                  <SelectValue placeholder="Select Subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select Subject</SelectItem>
                  {subjects.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">Exam Type</Label>
              <Select value={examType || 'none'} onValueChange={(v) => setExamType(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="marks-select-exam">
                  <SelectValue placeholder="Select Exam Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select Exam</SelectItem>
                  {examOptions.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedClass && selectedSubject && examType && (
        <Card className="border border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Enter {examLabel} component marks
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="border-red-500/40 text-red-500 hover:bg-red-500/10"
                  onClick={handleResetEntries}
                  disabled={resetting}
                  data-testid="reset-marks-button"
                >
                  {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Reset Entries
                </Button>
                <Button onClick={handleSaveMarks} disabled={saving} data-testid="save-marks-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Marks
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Caps — {componentLabels.pt}: 5 · {componentLabels.sea}: 5 · {componentLabels.pf}: 5 · {componentLabels.ma}: 5 · {componentLabels.mt}: 80 · TOTAL out of 100
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : students.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Roll</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>{componentLabels.pt} (5)</TableHead>
                      <TableHead>{componentLabels.sea} (5)</TableHead>
                      <TableHead>{componentLabels.pf} (5)</TableHead>
                      <TableHead>{componentLabels.ma} (5)</TableHead>
                      <TableHead>{componentLabels.mt} (80)</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>%</TableHead>
                      <TableHead className="text-right">Report Card</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((student) => {
                      const row = compositeData[student.id] || {};
                      const total = componentSum(row);
                      const pct = COMPOSITE_TOTAL > 0 ? Math.round((total / COMPOSITE_TOTAL) * 100) : 0;
                      const status = reportStatus[student.id];
                      return (
                        <TableRow key={student.id} className="table-row-hover">
                          <TableCell>{student.roll_number}</TableCell>
                          <TableCell className="font-medium">{student.name}</TableCell>
                          {['pt', 'sea', 'pf', 'ma', 'mt'].map((k) => (
                            <TableCell key={k}>
                              <Input
                                type="number"
                                value={row[k] ?? ''}
                                onChange={(e) => handleComponentChange(student.id, k, e.target.value)}
                                min={0}
                                max={COMPOSITE_CAPS[k]}
                                className="w-20"
                                data-testid={`marks-${k}-${student.id}`}
                              />
                            </TableCell>
                          ))}
                          <TableCell className="font-semibold">{total.toFixed(1)}</TableCell>
                          <TableCell>
                            <span className={pct >= 60 ? 'text-green-500' : pct >= 40 ? 'text-yellow-500' : 'text-red-500'}>
                              {pct}%
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {isAdmin ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadReport(student)}
                                title={status && !status.ready
                                  ? `Some marks missing — preview will show blanks for: ${status.missing.slice(0, 4).join(', ')}${status.missing.length > 4 ? ' …' : ''}`
                                  : 'Download report card'}
                                data-testid={`download-reportcard-${student.id}`}
                              >
                                {status && !status.ready
                                  ? <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                  : <Download className="h-4 w-4" />}
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground" data-testid={`download-blocked-${student.id}`}>
                                Admin only
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No students in this class</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedClass && (
        <Card className="border border-border">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Enter Student Marks</h3>
            <p className="text-muted-foreground">
              Select a class, subject, and exam type to start entering marks
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MarksPage;
